// ════════════════════════════════════════════════════════════════════════
//  DISPOSITIVO — ¿celular, tablet o computador?
//
//  Dos preguntas distintas, dos respuestas:
//
//    IS_MOBILE → ¿es un aparato táctil de bolsillo o tableta (Android, iPhone,
//                iPad)? Decide QUÉ se puede hacer: ahí el navegador no deja
//                capturar el audio de otra pestaña (getDisplayMedia no existe o
//                no trae audio) ni abrir el panel flotante (Document PiP es
//                solo de Chrome de escritorio). Quedan el Reproductor y la voz.
//
//    IS_PHONE  → ¿además la pantalla es de teléfono? Decide CÓMO se pinta: una
//                sola columna, menos relleno, sin catálogo lateral. Una tablet
//                es IS_MOBILE pero no IS_PHONE: tiene sitio para el rack de PC.
//
//  Se calcula una vez al cargar: el aparato no cambia en caliente. Se mide con
//  el lado CORTO de la pantalla física (screen), no con el ancho de la ventana,
//  para que un teléfono girado en horizontal siga siendo teléfono y una ventana
//  estrecha de PC siga siendo PC — en el computador nada cambia.
//
//  Además deja data-device en <html> para las reglas CSS que no pueden vivir
//  en estilos inline (ver MusicPlayer.css), igual que data-theme.
// ════════════════════════════════════════════════════════════════════════

const nav = typeof navigator !== 'undefined' ? navigator : {};
const ua = nav.userAgent || '';

// iPadOS 13+ se presenta como "Macintosh": lo delata la pantalla táctil.
const isIPadOS = /Macintosh/.test(ua) && (nav.maxTouchPoints || 0) > 1;

function detectMobile() {
  // Client Hints (Chrome/Edge Android): la respuesta oficial cuando existe.
  if (nav.userAgentData && nav.userAgentData.mobile) return true;
  return /Android|iPhone|iPad|iPod|Mobi|Silk|Opera Mini|IEMobile/i.test(ua) || isIPadOS;
}

function detectPhone(mobile) {
  if (!mobile || typeof window === 'undefined' || !window.screen) return false;
  const corto = Math.min(window.screen.width || 0, window.screen.height || 0);
  return corto > 0 && corto < 600;
}

export const IS_MOBILE = detectMobile();
export const IS_PHONE = detectPhone(IS_MOBILE);

// Imágenes ligeras para el teléfono (public/img/m, WebP): los PNG originales
// pesan megas y en datos móviles eran lo que más tardaba en aparecer. El PC
// sigue con los originales.
export const phoneImg = (pc, phone) => (IS_PHONE ? phone : pc);

// ── Equipo modesto (PC viejo / portátil básico) ──
// ≤ 4 hilos de CPU o ≤ 4 GB de RAM (deviceMemory solo existe en Chrome/Edge;
// donde no está, decide la CPU). Un i5 de 4 núcleos con hyper-threading da 8
// hilos y NO entra: esto apunta a los doble núcleo y a los equipos con poca RAM,
// donde fondo animado + vídeo + rack se reparten la misma CPU que el audio.
// Solo afina cosas internas (búfer de audio, tamaño de los FFT): ya NO apaga
// visuales ni arranca en Simple. "Menos animación" del sistema no cuenta: en
// Windows 10 lo tiene medio mundo sin saberlo y ya tiene su data-motion. Brave
// y Firefox anti-rastreo mienten con los hilos, así que lo que de verdad
// recomienda el Modo Ligero es medir la fluidez (LiteHint.js).
function detectLowPower() {
  const hilos = nav.hardwareConcurrency || 0;
  const ram = nav.deviceMemory || 0;
  const pocaCpu = hilos > 0 && hilos <= 4;
  const pocaRam = ram > 0 && ram <= 4;
  return pocaCpu || pocaRam;
}

export const IS_LOW_POWER = detectLowPower();

// ── Modo Ligero (botón ⚡ del Reproductor) ──
// <html data-lite> lo leen los CSS (lite.css y cada componente) y los bucles
// de dibujo (isLite() en cada frame: cambiar de modo no recrea nada). Misma
// clave que STORAGE_KEYS.visualMode (api.js): 'simple' = Ligero.
const LITE_KEY = 'dragonVisualMode';

export function isLite() {
  return typeof document !== 'undefined' && document.documentElement.hasAttribute('data-lite');
}

export function setLite(on) {
  if (on) document.documentElement.setAttribute('data-lite', '');
  else document.documentElement.removeAttribute('data-lite');
}

// ── Animaciones de la interfaz: 'full' | 'reduce' ──
// <html data-motion> lo leen los CSS (de momento, Fuxi). Por defecto sigue al
// sistema: en Windows 10, "Mostrar animaciones en Windows" apagado (o
// Rendimiento → "mejor rendimiento") hace que Edge/Chrome pidan menos
// movimiento, y muchos usuarios lo tienen así sin saberlo. Por eso el usuario
// puede elegir en la app (botón ✦ de Fuxi) y su elección manda sobre el sistema.
const MOTION_KEY = 'dragonMotion';

export function systemReducesMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** true si el usuario ya eligió en la app (entonces el sistema no manda). */
export function hasMotionChoice() {
  try { return ['full', 'reduce'].includes(localStorage.getItem(MOTION_KEY)); } catch (e) { return false; }
}

export function getMotion() {
  try {
    const v = localStorage.getItem(MOTION_KEY);
    if (v === 'full' || v === 'reduce') return v;
  } catch (e) { /* almacenamiento bloqueado: se sigue al sistema */ }
  return systemReducesMotion() ? 'reduce' : 'full';
}

export function setMotion(v) {
  try { localStorage.setItem(MOTION_KEY, v); } catch (e) { /* sin almacenamiento: vale para esta visita */ }
  document.documentElement.dataset.motion = v;
}

export function initDevice() {
  document.documentElement.dataset.device = IS_PHONE ? 'phone' : IS_MOBILE ? 'tablet' : 'desktop';
  document.documentElement.dataset.motion = getMotion();
  let lite = false;
  try { lite = localStorage.getItem(LITE_KEY) === 'simple'; } catch (e) { /* sin almacenamiento: modo completo */ }
  setLite(lite);
}
